import type { RouteResponse } from '@/shared/api/routes'
import type { MapboxDirectionsResponse } from '../services/mapboxDirectionsService'

// Color palette for different days (matching useItineraryBuilder.ts)
const DAY_COLORS = [
  '#5C8DC5', // Primary blue - Day 1
  '#14b8a6', // Teal - Day 2 (high contrast with roads and map)
  '#AD9E90', // Accent 1 - Day 3
  '#736F60', // Accent 2 - Day 4
  '#8B9DC3', // Light blue - Day 5
  '#A89B8D', // Light brown - Day 6
  '#9B9B9B', // Gray - Day 7
]

/**
 * Convert Mapbox Directions response to RouteResponse format
 * 
 * @param mapboxRoute - Route from Mapbox Directions API response
 * @param day - Day number for itinerary organization
 * @param waypointIds - Array of waypoint IDs in order
 * @param tripId - Trip ID
 * @param waypointNames - Array of waypoint names in order (optional)
 * @returns RouteResponse object compatible with existing code
 */
export function convertMapboxRouteToRouteResponse(
  mapboxRoute: MapboxDirectionsResponse['routes'][0],
  day: number,
  waypointIds: string[],
  tripId: string,
  waypointNames: string[] = []
): RouteResponse {
  // Generate unique route ID
  const routeId = `day-${day}-${tripId}-${Date.now()}`

  // Generate route name
  const routeName = `Day ${day}`

  // Get color for this day (cycle through colors if day > 7)
  const color = DAY_COLORS[(day - 1) % DAY_COLORS.length]

  // Create route response
  const routeResponse: RouteResponse = {
    id: routeId,
    name: routeName,
    description: waypointNames.length > 0 
      ? `Route through ${waypointNames.join(', ')}`
      : `Route for day ${day}`,
    geometry: mapboxRoute.geometry,
    distance: mapboxRoute.distance, // meters
    duration: mapboxRoute.duration, // seconds
    is_public: false,
    created_by: '', // Not applicable for client-generated routes
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // Styling
    color,
    line_type: 'solid',
    line_width: 4,
    // Day metadata
    day,
    // Waypoint metadata
    waypoint_count: waypointIds.length,
    // Store waypoint IDs in metadata for tracking
    metadata: {
      waypoint_ids: waypointIds,
      waypoint_names: waypointNames,
      generated_at: new Date().toISOString(),
      source: 'mapbox_directions_api'
    }
  }

  return routeResponse
}

